<script setup>
/**
 * Публичный раздел «Судебная практика».
 *
 * Это ответ на задачу «пусть Google поймёт, что мы ведём базу практики».
 * Форма добавления для этого не годится: она за авторизацией и роботу отдаёт
 * редирект. Поисковик видит только опубликованное — значит, опубликовать надо
 * сами дела.
 *
 * Разметку (schema.org) сюда добавим отдельно: сначала страница, потом язык,
 * которым мы её объясняем машине.
 */
import MainHeader from "@/Layouts/MainHeader.vue";
import MainFooter from "@/Layouts/MainFooter.vue";
import Body from "@/Layouts/Body.vue";
import Pagination from "@/Components/Pagination.vue";
import { Head, router } from "@inertiajs/vue3";
import { computed, ref } from "vue";

const props = defineProps({
  cases: Object,
  catalogue: Array,
  regions: Object,
  filters: Object,
  active: { type: Object, default: null },
  cities: { type: Array, default: () => [] },
  activeCity: { type: Object, default: null },
  indexable: { type: Boolean, default: true },
  canonical: { type: String, default: "" },
  stats: Object,
  actsCount: { type: Number, default: 0 },
  // Приходит только своим. У гостя здесь null, и в разметку ничего лишнего не
  // попадает: props уезжают в HTML целиком.
  admin: { type: Object, default: null },
  adminFilters: { type: Object, default: null },
  auth: Object,
});

// ── Режим редакции ──────────────────────────────────────────────────────────
/**
 * ФИЛЬТРЫ РЕДАКЦИИ — ОТДЕЛЬНЫМ БЛОКОМ.
 *
 * Раньше здесь были три вкладки «Черновики / В статистике / Отклонённые», и все
 * три фильтровали только дела квиза: 815 опубликованных решений в них не было
 * вовсе, а цифры 2 / 191 / 1 читались как «вся база». Теперь фильтры названы тем,
 * что они делают, и смотрят по всей базе.
 *
 * ⚠ ГОРОД И РЕГИОН ЗДЕСЬ — ПАРАМЕТРЫ, А НЕ АДРЕС. Адреса городов — страницы для
 * поисковика, служебные выборки их не плодят.
 */
const af = props.adminFilters;
const fm = ref(af ? {
  // Любой фильтр «по базе» без явного выбора — это вся база (так же решает сервер)
  where: af.where || (af.active ? "all" : ""),
  razbor: af.razbor || "",
  obst: af.obst || "",
  ist: af.ist || "",
  region: af.region || "",
  city: af.city || (props.activeCity ? props.activeCity.slug : ""),
  sud: af.sud || "",
  god: af.god ? String(af.god) : "",
  vid: af.vid || "",
  q: af.q || "",
} : null);

const WHERE = [
  { v: "", t: "Как видит читатель", n: "showcase",
    h: "Ровно то, что видит посетитель: опубликованные дела плюс те, что включены в статистику квиза." },
  { v: "all", t: "Вся база", n: "all",
    h: "Всё, что скачано и не снято вручную, — вместе с черновиками, у которых ещё нет страницы." },
  { v: "pub", t: "Опубликованы", n: "pub",
    h: "У дела есть страница на сайте: выдан адрес, оно в карте сайта и доступно поисковику." },
  { v: "unpub", t: "Не опубликованы", n: "unpub",
    h: "Лежат в складе без страницы: нет категории, уголовные, слишком короткие или не прошли повторное обезличивание." },
  { v: "hidden", t: "Сняты", n: "hidden",
    h: "Сняты вручную кнопкой «Снять»: ни на сайте, ни в счётчиках. Повторный прогон команд их не вернёт." },
];

/** Пояснение к выбранному «что показывать» — под строкой кнопок. */
const whereHint = computed(() => WHERE.find((o) => o.v === (fm.value?.where || ""))?.h || "");

/** Подсказки к остальным полям: чем именно фильтруем. */
const HINTS = {
  razbor: "Разбор — наш собственный текст: тема, суть спора, что установил суд и вывод. Без него страница остаётся копией акта и в карту сайта не идёт.",
  obst: "Обстоятельства — ответы по справочнику категории («срок пропущен», «прибор учёта поверен»). По ним считается статистика и подбираются похожие дела.",
  ist: "Заведено вручную через «+ Дело», а не привезено обходом судов.",
  region: "Регион суда, вынесшего акт. Города и суды ниже сужаются до выбранного региона.",
  city: "Город суда. Для редакции это параметр фильтра, отдельной страницы города он не открывает.",
  sud: "Конкретный суд. Список показывает только суды выбранного региона.",
  god: "Год вынесения акта, а не год публикации у нас.",
  vid: "Решение, определение, постановление, приговор — как акт назвал сам суд.",
  q: "Ищет по номеру дела, названию суда и теме разбора. Часть номера тоже подойдёт.",
};

/** Сколько фильтров включено — для подписи «Сбросить (N)». */
const activeCount = computed(() => {
  if (!fm.value) return 0;
  return Object.entries(fm.value).filter(([k, v]) => v !== "" && k !== "where").length
    + (fm.value.where ? 1 : 0);
});

/** Города выбранного региона. */
const citiesForRegion = computed(() => {
  const list = af?.facets?.cities || [];
  return fm.value?.region ? list.filter((c) => c.region === fm.value.region) : list;
});

// Регион из адреса, которого нет в базе, не оставляет пустой список
if (fm.value && fm.value.region && !(af.facets.regions || []).some((r) => r.code === fm.value.region)) {
  fm.value.region = "";
}

/** Суды выбранного региона — иначе в списке полсотни чужих. */
const courtsForRegion = computed(() => {
  const list = af?.facets?.courts || [];
  return fm.value?.region ? list.filter((c) => c.region === fm.value.region) : list;
});

// HINTS используется в шаблоне через :title
function filterPath() {
  // Город из адреса снимаем: в блоке он задаётся параметром
  return props.active ? `/sudebnaya-praktika/${props.active.url}` : "/sudebnaya-praktika";
}

function applyFilters(patch = {}) {
  Object.assign(fm.value, patch);
  const params = {};
  for (const [k, v] of Object.entries(fm.value)) if (v !== "") params[k] = v;

  // Выбор «по всей базе»-фильтра при режиме читателя переводит во «всю базу»:
  // иначе фильтр по разбору искал бы только среди опубликованного и путал
  const needsBase = ["razbor", "obst", "ist", "sud", "god", "vid", "q"].some((k) => params[k]);
  if (needsBase && !params.where) { params.where = "all"; fm.value.where = "all"; }

  router.get(filterPath(), params, { preserveScroll: true });
}

function resetFilters() {
  router.get(filterPath(), {}, { preserveScroll: true });
}

/** Ссылка на тему с сохранением включённых фильтров. */
function themeHref(url) {
  const path = url ? `/sudebnaya-praktika/${url}` : "/sudebnaya-praktika";
  if (!fm.value) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(fm.value)) if (v !== "") params.set(k, v);
  const q = params.toString();
  return q ? `${path}?${q}` : path;
}

/**
 * Быстрая смена статуса прямо из списка.
 *
 * Ревью — работа поточная: просмотрел карточку, отправил в статистику или снял.
 * Заставлять ради этого открывать редактор значит превращать десять решений в
 * десять переходов туда и обратно.
 */
function setStatus(id, status) {
  router.post(route("admin.court.status", id), { status }, { preserveScroll: true });
}


// ── Темы: буквы и поиск ─────────────────────────────────────────────────────
/**
 * Тем больше сотни в полутора десятках направлений, и найти свою в сплошном
 * столбце трудно. Буква оставляет направления и темы на неё, поле — по
 * вхождению слова. ⚠ Ссылки только ПРЯЧЕМ (v-show), а не убираем из разметки:
 * поисковик должен видеть все темы на странице.
 */
const themeLetter = ref(null);
const themeQuery = ref("");
const norm = (s) => (s || "").toLowerCase().replace(/ё/g, "е").trim();
const first = (s) => norm(s).charAt(0).toUpperCase();

const themeLetters = computed(() => {
  const set = new Set();
  for (const m of props.catalogue) {
    set.add(first(m.label));
    for (const s of m.subs || []) set.add(first(s.label));
  }
  return [...set].filter((l) => /[А-ЯA-Z]/.test(l)).sort((a, b) => a.localeCompare(b, "ru"));
});

function pickLetter(l) {
  themeLetter.value = themeLetter.value === l ? null : l;
  themeQuery.value = "";
}

/** Видна ли тема: совпала сама или совпало её направление. */
function subVisible(m, sub) {
  const q = norm(themeQuery.value);
  if (q) return norm(sub.label).includes(q) || norm(m.label).includes(q);
  if (themeLetter.value) return first(m.label) === themeLetter.value || first(sub.label) === themeLetter.value;
  return true;
}
const groupVisible = (m) => (m.subs || []).some((s) => subVisible(m, s))
  || (!!norm(themeQuery.value) && norm(m.label).includes(norm(themeQuery.value)));
const themeFiltering = computed(() => !!themeLetter.value || !!norm(themeQuery.value));
const themesFound = computed(() => props.catalogue.some((m) => groupVisible(m)));

// ── Город ───────────────────────────────────────────────────────────────────
/**
 * Городов полтора десятка, и будет больше. Список целиком — это стена ссылок,
 * поэтому: поле для ввода и ссылки на те, где практики больше всего. Ссылки
 * настоящие, а не переходы по скрипту, — иначе поисковик их не обойдёт, и
 * страницы городов останутся вне индекса.
 */
const cityQuery = ref("");
const cityMatches = computed(() => {
  const q = cityQuery.value.trim().toLowerCase().replace(/ё/g, "е");
  const list = props.cities;
  if (!q) return list.slice(0, 8);

  return list.filter((c) => c.name.toLowerCase().replace(/ё/g, "е").includes(q)).slice(0, 8);
});

function cityHref(slug) {
  if (!slug) return props.active ? `/sudebnaya-praktika/${props.active.url}` : "/sudebnaya-praktika";
  return props.active
    ? `/sudebnaya-praktika/${props.active.url}/${slug}`
    : `/sudebnaya-praktika/gorod/${slug}`;
}

const title = computed(() => {
  const theme = props.active ? props.active.name.toLowerCase() : null;
  const city = props.activeCity ? props.activeCity.name : null;

  // Город идёт в заголовок так, как его ищут: «в Симферополе», а не «Симферополь».
  if (theme && city) return `Судебная практика: ${theme} в городе ${city}`;
  if (city)          return `Судебная практика судов города ${city}: решения по делам`;
  if (theme)         return `Судебная практика: ${theme} — решения судов`;

  // Перечень тем в заголовке — по тому, чего в базе больше всего. Было «по семейным
  // и автомобильным»: трудовых и рекламных дел тогда не существовало, а теперь их больше.
  return "Судебная практика: решения судов по трудовым, семейным и рекламным спорам";
});

const description = computed(() => {
  const city = props.activeCity ? props.activeCity.name : null;

  if (props.active && city) {
    return `Решения судов города ${city} по теме «${props.active.name}»: суд, номер дела, дата и чем закончилось. Дел в подборке — ${props.cases.total}.`;
  }
  if (city) {
    return `Решения судов города ${city}: ${props.cases.total} дел с указанием суда, номера и исхода. Всё проверяется по картотеке.`;
  }
  if (props.active) {
    return `Реальные решения судов по теме «${props.active.name}»: суд, номер дела, дата и чем закончилось. Разбор практики от юристов nedicom.ru.`;
  }

  return `База решений судов Крыма, Севастополя и Краснодарского края: ${props.stats.cases} дел, ${props.stats.courts} судов. Суд, номер дела, дата и исход — всё проверяется по картотеке.`;
});

const casesWord = (n) => {
  const t = n % 100, o = n % 10;
  if (t >= 11 && t <= 14) return "дел";
  return o === 1 ? "дело" : (o >= 2 && o <= 4) ? "дела" : "дел";
};

function regionHref(code) {
  const base = props.activeCity
    ? cityHref(props.activeCity.slug)
    : (props.active ? `/sudebnaya-praktika/${props.active.url}` : "/sudebnaya-praktika");
  // ⚠ НА СТРАНИЦЕ ГОРОДА ПАРАМЕТР РЕГИОНА ИЗБЫТОЧЕН. Город уже сузил выборку, а
  // ссылка вида /gorod/moskva?region=msk плодила копию той же страницы — Google
  // проиндексировал её как отдельную, каноникал не помог.
  if (!code || (props.activeCity && props.activeCity.region === code)) return base;
  return `${base}?region=${code}`;
}
</script>

<template>
  <Head>
    <title>{{ title }}</title>
    <meta name="description" :content="description" />
    <link rel="canonical" :href="canonical" />
    <!--
      Пересечение темы и города с парой дел в индекс не пускаем: заголовок
      обещает практику, а под ним два решения. Ссылки при этом обходить можно —
      отсюда follow.
    -->
    <meta v-if="!indexable" name="robots" content="noindex, follow" />
  </Head>

  <MainHeader :auth="auth" />

  <Body>
    <main class="bg-gray-50 min-h-screen">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 py-8 md:py-12">

        <!--
          ПАНЕЛЬ РЕДАКЦИИ. Раньше для этого была отдельная страница со своей
          таблицей: она показывала те же дела по другим правилам, и списки
          расходились. Теперь список один, а у редакции — только фильтр по
          статусу и правка.
        -->
        <!--
          ⚠ ПАНЕЛЬ В ОДНУ СТРОКУ. Она служебная и стоит над самим разделом: занимать
          две строки и тянуть на себя внимание ей незачем. Поэтому мелкий шрифт,
          узкие отступы, подпись «Работа с базой» спрятана с мобильных, а если места
          всё же не хватает — строка прокручивается вбок, а не ломается.
        -->
        <div v-if="admin" class="mb-4 bg-white rounded-xl border border-indigo-100 shadow-sm px-3 py-2">
          <div class="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <span class="text-[11px] font-semibold uppercase tracking-wide text-indigo-600 mr-1">Работа с базой</span>

            <span class="flex-1"></span>

            <a v-if="admin.docsUrl" :href="admin.docsUrl"
               class="shrink-0 text-[13px] text-gray-500 hover:text-indigo-700 px-1">
              Как устроен раздел
            </a>
            <!-- Контурная, потому что реже нужна: дела заводят каждый день,
                 обстоятельства — когда появляется новая категория -->
            <a v-if="admin.factorsUrl" :href="admin.factorsUrl"
               class="shrink-0 rounded-lg border border-indigo-200 bg-white text-indigo-700 hover:border-indigo-400 text-[13px] font-semibold px-2.5 py-1">
              + Обстоятельства
            </a>
            <a :href="admin.addUrl"
               class="shrink-0 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] font-semibold px-2.5 py-1 transition">
              + Дело
            </a>
          </div>

        </div>

        <!-- ── ФИЛЬТРЫ РЕДАКЦИИ ───────────────────────────────────────────── -->
        <section v-if="fm" class="mb-5 bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div class="flex flex-wrap items-baseline justify-between gap-2 mb-3">
            <h2 class="m-0 text-sm font-bold text-gray-900">Фильтры</h2>
            <button v-if="activeCount" type="button" @click="resetFilters"
                    class="text-xs font-semibold text-indigo-700 hover:underline">
              Сбросить ({{ activeCount }})
            </button>
          </div>

          <!-- Что показывать -->
          <div class="flex flex-wrap gap-1.5">
            <button v-for="o in WHERE" :key="o.v" type="button" @click="applyFilters({ where: o.v })"
                    :title="o.h"
                    class="rounded-lg border px-2.5 py-1 text-[13px] transition-colors"
                    :class="fm.where === o.v
                      ? 'border-indigo-500 bg-indigo-50 font-semibold text-indigo-700'
                      : 'border-gray-200 text-gray-700 hover:border-indigo-300'">
              {{ o.t }}
              <span class="ml-0.5 text-[11px] text-gray-400">{{ af.facets.counts[o.n] }}</span>
            </button>
          </div>

          <p v-if="whereHint" class="mt-2 text-[11px] leading-snug text-gray-500">{{ whereHint }}</p>

          <!-- Состояние дела -->
          <div class="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label class="block">
              <span class="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Разбор
                <span class="ml-0.5 cursor-help text-gray-400" :title="HINTS.razbor">?</span>
              </span>
              <select v-model="fm.razbor" @change="applyFilters()" class="w-full rounded-lg border-gray-200 text-sm py-1.5">
                <option value="">Любой</option>
                <option value="yes">Есть разбор ({{ af.facets.counts.razbor }})</option>
                <option value="no">Без разбора ({{ af.facets.counts.norazbor }})</option>
              </select>
            </label>
            <label class="block">
              <span class="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Обстоятельства
                <span class="ml-0.5 cursor-help text-gray-400" :title="HINTS.obst">?</span>
              </span>
              <select v-model="fm.obst" @change="applyFilters()" class="w-full rounded-lg border-gray-200 text-sm py-1.5">
                <option value="">Любые</option>
                <option value="yes">Проставлены ({{ af.facets.counts.obst }})</option>
                <option value="no">Не проставлены ({{ af.facets.counts.noobst }})</option>
              </select>
            </label>
            <label class="block">
              <span class="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Кто завёл
                <span class="ml-0.5 cursor-help text-gray-400" :title="HINTS.ist">?</span>
              </span>
              <select v-model="fm.ist" @change="applyFilters()" class="w-full rounded-lg border-gray-200 text-sm py-1.5">
                <option value="">Все</option>
                <option value="manual">Вручную ({{ af.facets.counts.manual }})</option>
              </select>
            </label>
          </div>

          <!-- Где и когда -->
          <div class="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <label class="block">
              <span class="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Регион
                <span class="ml-0.5 cursor-help text-gray-400" :title="HINTS.region">?</span>
              </span>
              <select v-model="fm.region" @change="applyFilters({ sud: '', city: '' })" class="w-full rounded-lg border-gray-200 text-sm py-1.5">
                <option value="">Все регионы</option>
                <option v-for="r in af.facets.regions" :key="r.code" :value="r.code">{{ r.name }} ({{ r.count }})</option>
              </select>
            </label>
            <label class="block">
              <span class="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Город
                <span class="ml-0.5 cursor-help text-gray-400" :title="HINTS.city">?</span>
              </span>
              <select v-model="fm.city" @change="applyFilters()" class="w-full rounded-lg border-gray-200 text-sm py-1.5">
                <option value="">Все города</option>
                <option v-for="c in citiesForRegion" :key="c.slug" :value="c.slug">{{ c.name }} ({{ c.count }})</option>
              </select>
            </label>
            <label class="block lg:col-span-2">
              <span class="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Суд
                <span class="ml-0.5 cursor-help text-gray-400" :title="HINTS.sud">?</span>
              </span>
              <select v-model="fm.sud" @change="applyFilters()" class="w-full rounded-lg border-gray-200 text-sm py-1.5">
                <option value="">Все суды{{ fm.region ? ' региона' : '' }}</option>
                <option v-for="c in courtsForRegion" :key="c.name + c.region" :value="c.name">{{ c.name }} ({{ c.count }})</option>
              </select>
            </label>
            <label class="block">
              <span class="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Год решения
                <span class="ml-0.5 cursor-help text-gray-400" :title="HINTS.god">?</span>
              </span>
              <select v-model="fm.god" @change="applyFilters()" class="w-full rounded-lg border-gray-200 text-sm py-1.5">
                <option value="">Любой</option>
                <option v-for="y in af.facets.years" :key="y.year" :value="String(y.year)">{{ y.year }} ({{ y.count }})</option>
              </select>
            </label>
          </div>

          <div class="mt-3 grid gap-3 sm:grid-cols-[1fr_auto] lg:grid-cols-[14rem_1fr]">
            <label class="block">
              <span class="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Вид акта
                <span class="ml-0.5 cursor-help text-gray-400" :title="HINTS.vid">?</span>
              </span>
              <select v-model="fm.vid" @change="applyFilters()" class="w-full rounded-lg border-gray-200 text-sm py-1.5">
                <option value="">Любой</option>
                <option v-for="t in af.facets.types" :key="t.name" :value="t.name">{{ t.name }} ({{ t.count }})</option>
              </select>
            </label>
            <form class="block" @submit.prevent="applyFilters()">
              <span class="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Поиск
                <span class="ml-0.5 cursor-help text-gray-400" :title="HINTS.q">?</span>
              </span>
              <div class="flex gap-2">
                <input v-model="fm.q" type="search" placeholder="Номер дела, суд или тема"
                       class="min-w-0 flex-1 rounded-lg border-gray-200 text-sm py-1.5" />
                <button type="submit" class="shrink-0 rounded-lg bg-indigo-600 px-3 text-sm font-semibold text-white hover:bg-indigo-700">
                  Найти
                </button>
              </div>
            </form>
          </div>
        </section>

        <!-- Шапка раздела -->
        <nav v-if="active || activeCity" class="text-sm text-gray-500 mb-3">
          <a href="/sudebnaya-praktika" class="hover:text-indigo-700">Судебная практика</a>
          <template v-if="active">
            <span class="mx-1.5">/</span>
            <a v-if="activeCity" :href="'/sudebnaya-praktika/' + active.url" class="hover:text-indigo-700">{{ active.name }}</a>
            <span v-else class="text-gray-700">{{ active.name }}</span>
          </template>
          <template v-if="activeCity">
            <span class="mx-1.5">/</span>
            <span class="text-gray-700">{{ activeCity.name }}</span>
          </template>
        </nav>

        <h1 class="text-2xl md:text-4xl font-extrabold text-gray-900 tracking-tight leading-tight">
          <template v-if="active && activeCity">Судебная практика: {{ active.name.toLowerCase() }} в городе {{ activeCity.name }}</template>
          <template v-else-if="activeCity">Судебная практика судов города {{ activeCity.name }}</template>
          <template v-else-if="active">Судебная практика: {{ active.name.toLowerCase() }}</template>
          <template v-else>Судебная практика</template>
        </h1>
        <p class="mt-3 text-gray-600 max-w-3xl leading-relaxed">
          Мы разбираем решения судов Крыма, Севастополя и Краснодарского края и держим их в общей базе:
          что было заявлено, какие обстоятельства суд установил и чем дело закончилось. У каждого дела
          стоит суд, номер и дата — их можно проверить в картотеке, не веря нам на слово.
        </p>

        <!-- Цифры -->
        <dl class="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-200 rounded-xl overflow-hidden">
          <div class="bg-white px-4 py-3">
            <dt class="text-xs text-gray-500">Дел в базе</dt>
            <dd class="text-xl font-extrabold text-gray-900">{{ stats.cases }}</dd>
          </div>
          <div class="bg-white px-4 py-3">
            <dt class="text-xs text-gray-500">Судов</dt>
            <dd class="text-xl font-extrabold text-gray-900">{{ stats.courts }}</dd>
          </div>
          <div class="bg-white px-4 py-3">
            <dt class="text-xs text-gray-500">Регионов</dt>
            <dd class="text-xl font-extrabold text-gray-900">{{ stats.regions }}</dd>
          </div>
          <div class="bg-white px-4 py-3">
            <dt class="text-xs text-gray-500">Направлений</dt>
            <dd class="text-xl font-extrabold text-gray-900">{{ catalogue.length }}</dd>
          </div>
        </dl>

        <!--
          Ссылка во второй подраздел. Здесь дела разложены по темам — то, с чем
          человек приходит («лишили прав», «пришёл штраф с камеры»); там те же
          акты списком по направлениям и годам. Один вход по вопросу, другой по
          реквизитам.
        -->
        <a v-if="actsCount" href="/sudebnaya-praktika/resheniya"
           class="mt-4 flex items-center justify-between gap-4 bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 hover:border-indigo-200 transition-colors">
          <span>
            <span class="block text-sm font-semibold text-indigo-700">Тексты решений — {{ actsCount }}</span>
            <span class="block mt-0.5 text-xs text-gray-500">
              Судебные акты целиком, как их опубликовал суд, — обезличенные.
            </span>
          </span>
          <span class="text-indigo-700 text-lg leading-none">→</span>
        </a>

        <div class="mt-8 lg:flex lg:gap-8">
          <!-- Каталог тем: ссылки, а не скрипт, — иначе поисковик их не обойдёт -->
          <aside class="lg:w-72 lg:shrink-0">
            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div class="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Темы</div>

              <input v-model="themeQuery" type="search" inputmode="search"
                     placeholder="Найти тему: наследство, увольнение…"
                     aria-label="Поиск темы"
                     @input="themeLetter = null"
                     class="w-full rounded-lg border-gray-200 text-sm mb-2" />

              <!-- Буквы: только те, на которые есть направления или темы -->
              <div class="flex flex-wrap gap-1 mb-3" role="group" aria-label="Темы по первой букве">
                <button v-for="l in themeLetters" :key="l" type="button"
                        @click="pickLetter(l)"
                        :aria-pressed="themeLetter === l"
                        class="min-w-[1.75rem] h-7 px-1 rounded-lg text-xs font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
                        :class="themeLetter === l ? 'bg-indigo-600 text-white' : 'bg-gray-50 text-gray-600 hover:bg-indigo-50 hover:text-indigo-700'">
                  {{ l }}
                </button>
                <button v-if="themeFiltering" type="button"
                        @click="themeLetter = null; themeQuery = ''"
                        class="h-7 px-2 rounded-lg text-xs text-gray-500 hover:text-indigo-700">
                  Сбросить
                </button>
              </div>

              <a v-show="!themeFiltering" :href="themeHref(null)"
                 class="block px-3 py-2 rounded-lg text-sm transition-colors"
                 :class="!active ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'">
                Все дела
                <span class="float-right text-xs text-gray-400">{{ stats.cases }}</span>
              </a>

              <p v-if="themeFiltering && !themesFound" class="px-3 py-2 text-sm text-gray-500">
                Такой темы пока нет.
              </p>

              <div v-for="m in catalogue" :key="m.id" v-show="groupVisible(m)" class="mt-3">
                <div class="px-3 text-xs font-semibold text-gray-500">{{ m.label }}</div>
                <a v-for="sub in m.subs" :key="sub.id" v-show="subVisible(m, sub)"
                   :href="sub.url ? themeHref(sub.url) : '#'"
                   class="block px-3 py-1.5 rounded-lg text-sm transition-colors"
                   :class="active && active.id === sub.id
                     ? 'bg-indigo-50 text-indigo-700 font-semibold'
                     : (sub.count ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-400 hover:bg-gray-50')">
                  {{ sub.label }}
                  <span class="float-right text-xs" :class="sub.count ? 'text-gray-400' : 'text-gray-300'">{{ sub.count }}</span>
                </a>
              </div>
            </div>

            <!--
              ГОРОД. Поле ввода — чтобы не листать список, ссылки под ним — чтобы
              их обошёл поисковик: страницы городов должны попадать в индекс
              своим ходом, а не только через переходы по скрипту.
            -->
            <div v-if="cities.length && !fm" class="mt-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div class="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Город суда</div>

              <input v-model="cityQuery" type="search" inputmode="search"
                     placeholder="Начните вводить город"
                     aria-label="Поиск города"
                     class="w-full rounded-lg border-gray-200 text-sm mb-2" />

              <a v-if="activeCity" :href="cityHref(null)"
                 class="block px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                ← Все города
              </a>

              <a v-for="c in cityMatches" :key="c.slug" :href="cityHref(c.slug)"
                 class="block px-3 py-1.5 rounded-lg text-sm transition-colors"
                 :class="activeCity && activeCity.slug === c.slug
                   ? 'bg-indigo-50 text-indigo-700 font-semibold'
                   : 'text-gray-700 hover:bg-gray-50'">
                {{ c.name }}
                <span class="float-right text-xs text-gray-400">{{ c.count }}</span>
              </a>

              <p v-if="cityQuery && !cityMatches.length" class="px-3 py-2 text-sm text-gray-500">
                По этому названию практики пока нет.
              </p>
            </div>

            <div v-if="!fm" class="mt-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div class="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Регион</div>
              <a :href="regionHref(null)" class="block px-3 py-1.5 rounded-lg text-sm transition-colors"
                 :class="!filters.region ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'">
                Все регионы
              </a>
              <a v-for="(label, code) in regions" :key="code" :href="regionHref(code)"
                 class="block px-3 py-1.5 rounded-lg text-sm transition-colors"
                 :class="filters.region === code ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'">
                {{ label }}
              </a>
            </div>
          </aside>

          <!-- Дела -->
          <div class="mt-6 lg:mt-0 flex-1 min-w-0">
            <p class="text-sm text-gray-500 mb-3">
              <template v-if="fm && activeCount">Найдено: </template>{{ cases.total }} {{ casesWord(cases.total) }}
              <template v-if="active"> по теме «{{ active.name.toLowerCase() }}»</template>
              <template v-if="activeCity"> в городе {{ activeCity.name }}</template>
            </p>

            <ol v-if="cases.data.length" class="space-y-3">
              <li v-for="c in cases.data" :key="c.id"
                  class="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-5">
                <div class="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <span class="inline-block rounded-lg bg-emerald-50 text-emerald-800 text-xs font-semibold px-2.5 py-1">
                    {{ c.outcome }}
                  </span>
                  <span class="text-xs text-gray-500">{{ c.category }}</span>
                  <span class="text-xs text-gray-400">·</span>
                  <span class="text-xs text-gray-500">{{ c.region }}</span>
                  <!-- Служебные значки: только своим -->
                  <template v-if="c.flags">
                    <span v-if="c.flags.hidden" class="rounded-md bg-rose-50 px-1.5 py-0.5 text-[11px] font-medium text-rose-700">снято</span>
                    <span v-else-if="!c.flags.published" class="rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">не опубликовано</span>
                    <span class="rounded-md px-1.5 py-0.5 text-[11px] font-medium"
                          :class="c.flags.analyzed ? 'bg-indigo-50 text-indigo-700' : 'bg-amber-50 text-amber-700'">
                      {{ c.flags.analyzed ? 'разбор есть' : 'без разбора' }}
                    </span>
                    <span v-if="c.flags.factors" class="rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">
                      обстоятельств: {{ c.flags.factors }}
                    </span>
                    <span v-if="c.flags.type" class="text-[11px] text-gray-400">{{ c.flags.type }}</span>
                  </template>
                </div>

                <h2 class="mt-2 text-sm font-semibold text-gray-900">
                  Дело № {{ c.number }} <span class="font-normal text-gray-500">от {{ c.date }}</span>
                </h2>
                <div class="text-xs text-gray-600">{{ c.court }}</div>

                <p v-if="c.summary" class="mt-2 text-sm text-gray-700 leading-relaxed">{{ c.summary }}</p>

                <div class="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
                  <!--
                    Полный текст — на нашей странице, если дело уже выпущено.
                    Ссылка идёт первой: она удерживает читателя у нас, тогда как
                    карточка на сайте суда уводит.
                  -->
                  <a v-if="c.page" :href="c.page" class="text-indigo-700 font-medium hover:underline">
                    Текст решения
                  </a>
                  <a v-if="c.editUrl" :href="c.editUrl" class="text-gray-500 font-medium hover:text-indigo-700">
                    Править
                  </a>
                  <button v-if="c.status && c.status !== 'published'" type="button"
                          @click="setStatus(c.id, 'published')"
                          class="text-emerald-700 font-medium hover:underline">
                    В статистику
                  </button>
                  <button v-if="c.status && c.status !== 'rejected'" type="button"
                          @click="setStatus(c.id, 'rejected')"
                          class="text-gray-400 font-medium hover:text-rose-600">
                    Снять
                  </button>
                  <!-- Старый значок «черновик / снято» убран: он говорил про статистику квиза,
                       а читался как статус публикации. Публикацию и снятие показывают значки
                       в шапке карточки, статистику — фильтр «Статистика квиза». -->
                  <a v-if="c.service" :href="c.service" class="text-indigo-700 font-medium hover:underline">
                    Юристы по этой теме
                  </a>
                  <a v-if="c.url" :href="c.url" target="_blank" rel="nofollow noopener" class="text-gray-500 hover:text-gray-700">
                    Карточка дела на сайте суда
                  </a>
                </div>
              </li>
            </ol>

            <p v-else class="bg-white rounded-2xl border border-gray-100 p-8 text-center text-gray-500">
              По этой теме дел пока нет. Мы пополняем базу — загляните позже.
            </p>

            <Pagination v-if="cases.links && cases.total > cases.per_page" :links="cases.links" />
          </div>
        </div>

        <!-- Пояснение: и читателю, и поисковику, что это за раздел -->
        <section class="mt-10 bg-white rounded-2xl border border-gray-100 shadow-sm p-5 md:p-7">
          <h2 class="text-lg md:text-xl font-bold text-gray-900 mb-3">Как мы работаем с практикой</h2>
          <div class="text-sm md:text-base text-gray-700 leading-relaxed space-y-3 max-w-3xl">
            <p>
              Единица базы — дело, а не отдельный акт. Если спор прошёл несколько инстанций, мы храним
              одну запись с итоговым исходом, а реквизиты описывают решающий акт. Иначе одно и то же
              дело считалось бы дважды и статистика врала бы.
            </p>
            <p>
              По каждому делу мы отмечаем обстоятельства, которые установил суд: кто подал иск, есть ли
              несовершеннолетние дети, что сказала опека, по какой статье КоАП составлен протокол.
              Именно эти обстоятельства, а не общие слова, определяют исход похожего спора.
            </p>
            <p>
              Сумм в решениях чаще всего нет — их вымарывают при публикации. Поэтому мы считаем то, что
              переживает обезличивание: чем закончилось, с кем остался ребёнок, в долях или в твёрдой
              сумме назначены алименты, отменено ли постановление о лишении прав.
            </p>
            <p class="text-gray-500 text-sm">
              База — ориентир по сложившейся практике, а не обещание исхода. Похожие обстоятельства не
              гарантируют похожего решения: суд оценивает доказательства конкретного дела.
            </p>
          </div>
        </section>
      </div>
    </main>
  </Body>

  <MainFooter />
</template>
